import { FC } from 'react';
import { useSubscribeToInvalidationState } from 'react-relay';
import graphql from 'babel-plugin-relay/macro';

import LabelIcon from '@mui/icons-material/Label';
import Box from '@mui/material/Box';
import TableRow from '@mui/material/TableRow';
import Table from 'components/Table';
import TableCell from 'components/TableCell';
import FabDialog from 'components/FabDialog';
import Row from 'components/LabelListRow';
import Add from 'components/LabelListAdd';
import Sorting, { useSorting } from 'components/Sorting';

import { useAvoidSuspense, useDelete } from 'lib';

import Query, {
  LabelListRefetchQuery,
} from '__generated__/LabelListRefetchQuery.graphql';
import { LabelList_data$key } from '__generated__/LabelList_data.graphql';

type Props = {
  data: LabelList_data$key;
};

const LabelList: FC<Props> = ({ data }) => {
  const [{ labels }, { refetch, loading }] = useAvoidSuspense<
    LabelListRefetchQuery,
    LabelList_data$key
  >(
    graphql`
      fragment LabelList_data on Query
      @refetchable(queryName: "LabelListRefetchQuery")
      @argumentDefinitions(
        first: { type: "Int", defaultValue: 2147483647 }
        after: { type: "String" }
      ) {
        labels(first: $first, after: $after) {
          __id
          edges {
            node {
              id
              ...LabelListRow_label
            }
          }

          pageInfo {
            ...Table_pageInfo
          }
        }
      }
    `,
    data,
    Query
  );

  const connectionID = labels?.__id;

  const [deleteLabel, deleting] = useDelete(connectionID);

  const [sort, sorting] = useSorting(
    graphql`
      mutation LabelListSortMutation($positions: [EntityWithPositionInput!]!) {
        updateLabelPositions(positions: $positions)
      }
    `,
    connectionID
  );

  const ids = connectionID ? [connectionID] : [];
  useSubscribeToInvalidationState(ids, () => {
    refetch({});
  });

  const { edges, pageInfo } = labels ?? {};

  if (!edges) return null;

  return (
    <Box>
      <Sorting edges={edges} onSort={sort}>
        <Table
          pageInfo={pageInfo ?? null}
          loading={loading || sorting || deleting}
          icon={<LabelIcon />}
          head={
            <TableRow>
              <TableCell></TableCell>
              <TableCell>Pavadimas</TableCell>
              <TableCell center>Tipas</TableCell>
              <TableCell></TableCell>
            </TableRow>
          }>
          {edges?.map(({ node }) => (
            <Row
              sortDisabled={sorting}
              label={node}
              key={node.id}
              onDelete={deleteLabel}
            />
          ))}
        </Table>
      </Sorting>

      {connectionID && (
        <FabDialog fullWidth maxWidth="sm">
          <Add connectionID={connectionID} />
        </FabDialog>
      )}
    </Box>
  );
};

export default LabelList;
